<template>
  <div class="video-embed-modal">
    <div class="video-embed-modal-content">
      <div class="widget-group">
        <CustomInputValidation
          ref="widgetRef"
          v-model="selectedWidget"
          :input-type="CustomInputType.Multiselect"
          :validation-object="!selectedWidget ? CustomInputValidator.InputMultiselectRequired : {}"
          :label="t('playlist-edit-select-widget')"
          :asteriks="false"
          :show-error-message="true"
          :options="[]"
          :value-prop="'widget_id'"
          :label-key="'widget_name'"
          :async="apiService.widget.getWidgets"
          :async-prop="{
            list_only: true,
            sort_by: 'create_date',
            sort_order: SortOrder.Descending,
            status: 'active'
          }"
          :spread="'widgets'"
          :label-key-with-id="true"
          :track-by="['widget_id', 'widget_name']"
          :delay="-1"
          :close-on-select="true"
          :resolve-on-load="true"
          :can-deselect="false"
          :can-clear="false"
          :clear-on-select="true"
          :clear-on-blur="true"
          :object="false"
          :searchable="true"
          :tooltip="t('playlist-edit-select-widget-info')"
          :mode="'single'"
          :placeholder="t('playlist-edit-select-widget')"
          @change="selectWidget"
        />
      </div>
      <div class="code-section">
        <TextIconTooltip
          class="title" text-size="0.875rem" :text="t('widgets-get-code-and-embed-code-title')" :tooltip="t('embed-widget-code-info')"
          :teleport-to-body="true"
        />
        <div class="code-container" :class="{ disabled: !embedCode }">
          <div class="code-content">
            <pre>{{ embedCode || embedCodePlaceholder }}</pre>
          </div>
          <div class="copy-button">
            <ActionButtons class="copy-icon" :actions="copyActions" :is-visible="Boolean(embedCode)" :is-dots-menu="false" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services'
import { CustomInputType, SortOrder, CustomModalProps } from '@/interfaces'
import { getPlayerCDNDomain } from '@/services/utils'
import * as CustomInputValidator from '@/data/models/inputOptions/input'
import CustomInputValidation from '@/components/common/custom-inputs/CustomInputValidation.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import TextIconTooltip from '@/components/common/tooltips/TextIconTooltip.vue'

interface VideoEmbedExtraProps {
  videoId: string
  videoName: string
}

interface VideoEmbedModalProps extends CustomModalProps {
  extraProps?: VideoEmbedExtraProps
}

const props = defineProps<VideoEmbedModalProps>()
const store = useStore()
const { t } = useI18n()
const selectedWidget = ref<string | number | null>(null)
const embedCode = ref('')
const embedCodePlaceholder = t('embed-select-single-video-placeholder')

const canCopy = ref(true)
const widgetRef = ref()

const copyActions = [
  {
    label: 'copy',
    handler: copyToClipboard,
    translationKey: 'copy'
  }
]

function copyToClipboard() {
  if (!embedCode.value) return
  navigator.clipboard.writeText(embedCode.value)
  if (canCopy.value) {
    canCopy.value = false
    store.dispatch('messages/addMessage', {
      message: t('library-embed-copy-clipboard'),
      type: 'success'
    })
    setTimeout(() => {
      canCopy.value = true
    }, 2500)
  }
}

function selectWidget() {
  if (selectedWidget.value) {
    generateEmbedCode()
  }
}

async function generateEmbedCode() {
  const videoId = props.extraProps?.videoId
  if (!videoId) {
    console.log('No videoId, returning')
    return
  }

  const baseUrl = getPlayerCDNDomain()
  const scriptStart = '<script data-cfasync="false" async type="text/javascript" src="'
  const scriptEnd = '"></' + 'script>'

  const qs = new URLSearchParams()
  qs.append('vid', String(videoId))

  const url = `${baseUrl}/init/${String(selectedWidget.value)}.js?${qs.toString()}`
  embedCode.value = `${scriptStart}${url}${scriptEnd}`
}
</script>

<style lang="scss" scoped>
.video-embed-modal {
  width: 640px;
  margin-inline: 2px;
  gap: 0.25rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .video-embed-modal-content {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    gap: 1.5rem;
    .widget-group {
      width: 100%;

      .custom-input {
        margin: 0 !important;
      }
    }

    .code-section {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .title {
        color: $bodyText;
      }

      .code-container {
        display: flex;
        border: 1px solid $embedWidgetBorderColor;
        border-radius: 4px;
        overflow: hidden;

        .code-content {
          display: flex;
          width: 100%;
          padding: 16px 8px 16px 16px;

          pre {
            margin: 0;
            min-height: 48px;
            font-size: 12px;
            color: $embedWidgetCodeColor;
            line-height: 1.4;
            white-space: pre-wrap;
            word-break: break-all;
          }
        }

        .copy-button {
          display: flex;
          align-items: flex-start;
          padding: 0.5rem;

          :deep(.actions-container) {
            padding: 0;
          }
        }
      }
    }
  }
}
</style>
